/*
===============================================================================
ABS DEVELOPERS — CONTACT FLOW SYSTEM
File: assets/abs-contact-flow-v2.js

Purpose:
  Generic step-by-step form engine for the standalone contact-only pages:
  • Lahore
  • Islamabad
  • Karachi

Features:
  • Activates contact-only body mode (hides theme header/footer)
  • Dynamic Island progress navigator
  • One-question-at-a-time transitions
  • Option-card selection
  • Required-step validation
  • Next / Back navigation
  • Completed Dynamic Island steps can be revisited
  • Mobile Dynamic Island label + counter
  • Final contact-details validation
  • Demo mode + Google Sheets web-app submission mode
  • One-submission-per-campaign browser/device lock
  • Persistent localStorage + first-party cookie protection
  • Persistent anonymous device ID added to every submission
  • Reopening / refreshing a submitted campaign shows a locked state
  • Shopify Theme Editor re-initialization
  • No external libraries
===============================================================================
*/

(() => {
  'use strict';

  const ABS = window.ABSContactFlow || {};
  window.ABSContactFlow = ABS;

  ABS.version = '2.0.0';

  const SELECTORS = {
    page: '.abs-contact-page',
    form: '.abs-contact-form[data-abs-contact-form], form.abs-contact-form',
    viewport: '.abs-contact-form__viewport',
    step: '.abs-contact-step',

    island: '.abs-dynamic-island',
    islandStep: '.abs-island-step',
    islandProgress: '.abs-dynamic-island__progress',
    islandProgressBar: '.abs-dynamic-island__progress-bar',
    mobileCount: '[data-abs-mobile-count]',
    mobileLabel: '[data-abs-mobile-label]',

    option: '.abs-contact-option',
    optionGroup: '[data-abs-option-group]',

    next: '[data-abs-contact-next]',
    back: '[data-abs-contact-back]',

    stepCurrent: '[data-abs-contact-current]',
    stepTotal: '[data-abs-contact-total]',

    field: '.abs-contact-field',
    control: '.abs-contact-field__control',

    success: '.abs-contact-success'
  };

  const STATE = {
    instances: new WeakMap(),
    pages: new WeakSet()
  };

  const prefersReducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ==========================================================================
     1. HELPERS
     ========================================================================== */

  const qs = (selector, scope = document) => scope.querySelector(selector);

  const qsa = (selector, scope = document) =>
    Array.prototype.slice.call(scope.querySelectorAll(selector));

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const text = (value) => (value == null ? '' : String(value).trim());

  const dispatch = (element, name, detail = {}) => {
    if (!element) return;

    element.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail
      })
    );
  };

  const safeCSS = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return CSS.escape(value);
    }

    return String(value).replace(/(["\\])/g, '\\$1');
  };

  const setDisabled = (element, disabled) => {
    if (!element) return;

    if ('disabled' in element) {
      element.disabled = !!disabled;
    }

    element.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  /* ==========================================================================
     2. SUBMISSION / DEVICE PROTECTION
     ========================================================================== */

  const LOCK_PREFIX = 'abs_contact_submitted_v2:';
  const DEVICE_STORAGE_KEY = 'abs_contact_device_id_v2';
  const DEVICE_COOKIE_NAME = 'abs_contact_device_v2';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10; // 10 years

  const normalizeKey = (value) =>
    text(value)
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const getCookie = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const parts = document.cookie ? document.cookie.split(';') : [];

    for (const rawPart of parts) {
      const part = rawPart.trim();

      if (part.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(part.slice(prefix.length));
        } catch (_) {
          return part.slice(prefix.length);
        }
      }
    }

    return '';
  };

  const setCookie = (name, value, maxAge = COOKIE_MAX_AGE) => {
    const secure =
      window.location && window.location.protocol === 'https:'
        ? '; Secure'
        : '';

    document.cookie =
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}` +
      `; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  };

  const safeStorageGet = (key) => {
    try {
      return window.localStorage.getItem(key) || '';
    } catch (_) {
      return '';
    }
  };

  const safeStorageSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (_) {
      return false;
    }
  };

  const randomId = () => {
    try {
      if (
        window.crypto &&
        typeof window.crypto.randomUUID === 'function'
      ) {
        return window.crypto.randomUUID();
      }

      if (
        window.crypto &&
        typeof window.crypto.getRandomValues === 'function'
      ) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);

        return Array.from(bytes)
          .map((byte) => byte.toString(16).padStart(2, '0'))
          .join('');
      }
    } catch (_) {
      // Fall through to the timestamp fallback.
    }

    return (
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2) +
      '-' +
      Math.random().toString(36).slice(2)
    );
  };

  const ensureDeviceId = () => {
    let deviceId =
      safeStorageGet(DEVICE_STORAGE_KEY) ||
      getCookie(DEVICE_COOKIE_NAME);

    if (!deviceId) {
      deviceId = randomId();
    }

    safeStorageSet(DEVICE_STORAGE_KEY, deviceId);
    setCookie(DEVICE_COOKIE_NAME, deviceId);

    return deviceId;
  };

  const simpleHash = (value) => {
    let hash = 2166136261;
    const source = String(value || '');

    for (let index = 0; index < source.length; index += 1) {
      hash ^= source.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(36);
  };

  /* ==========================================================================
     3. CONTACT-ONLY PAGE MODE
     ========================================================================== */

  function activateContactOnlyMode() {
    document.body.classList.add('abs-contact-only-active');
    document.documentElement.classList.add('abs-contact-flow-loaded');
  }

  function deactivateContactOnlyModeIfUnused() {
    const remaining = document.querySelector(SELECTORS.page);

    if (!remaining) {
      document.body.classList.remove('abs-contact-only-active');
      document.documentElement.classList.remove('abs-contact-flow-loaded');
    }
  }

  /* ==========================================================================
     4. CONTACT FLOW CLASS
     ========================================================================== */

  class ContactFlow {
    constructor(page) {
      this.page = page;
      this.form = qs(SELECTORS.form, page);

      if (!this.form) return;

      this.viewport = qs(SELECTORS.viewport, this.form);
      this.steps = qsa(SELECTORS.step, this.form);

      this.island = qs(SELECTORS.island, page);
      this.islandSteps = qsa(SELECTORS.islandStep, page);
      this.islandProgress = qs(SELECTORS.islandProgress, page);
      this.islandProgressBar = qs(SELECTORS.islandProgressBar, page);

      this.mobileCount = qs(SELECTORS.mobileCount, page);
      this.mobileLabel = qs(SELECTORS.mobileLabel, page);

      this.currentEl = qs(SELECTORS.stepCurrent, this.form);
      this.totalEl = qs(SELECTORS.stepTotal, this.form);

      this.success = qs(SELECTORS.success, this.form);

      this.campaignKey = this.resolveCampaignKey();
      this.deviceId = ensureDeviceId();
      this.lockStorageKey = this.campaignKey
        ? `${LOCK_PREFIX}${this.campaignKey}`
        : '';
      this.lockCookieName = this.campaignKey
        ? `abs_submitted_${simpleHash(this.campaignKey)}`
        : '';

      this.currentIndex = this.getInitialIndex();
      this.highestVisitedIndex = this.currentIndex;
      this.animating = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleIslandPointerMove = this.handleIslandPointerMove.bind(this);
      this.handleIslandPointerLeave = this.handleIslandPointerLeave.bind(this);

      this.init();
    }

    /* ----------------------------------------------------------------------
       CAMPAIGN / DEVICE LOCK
       ---------------------------------------------------------------------- */

    resolveCampaignKey() {
      const explicit =
        this.form.dataset.absCampaignKey ||
        this.page.dataset.absCampaignKey;

      if (text(explicit)) {
        return normalizeKey(explicit);
      }

      const formKey = qs(
        'input[name="form_key"]',
        this.form
      );

      return normalizeKey(formKey ? formKey.value : '');
    }

    isSubmissionLocked() {
      if (!this.campaignKey) return false;

      const localValue = this.lockStorageKey
        ? safeStorageGet(this.lockStorageKey)
        : '';

      const cookieValue = this.lockCookieName
        ? getCookie(this.lockCookieName)
        : '';

      /*
        If either layer still has the submission marker, restore the other.
        This makes normal refresh/reopen behaviour resilient if one storage
        mechanism is unavailable or was removed independently.
      */
      const lockValue = localValue || cookieValue;

      if (!lockValue) return false;

      if (this.lockStorageKey && !localValue) {
        safeStorageSet(this.lockStorageKey, lockValue);
      }

      if (this.lockCookieName && !cookieValue) {
        setCookie(this.lockCookieName, lockValue);
      }

      return true;
    }

    markSubmissionLocked(values = {}) {
      if (!this.campaignKey) return;

      const record = JSON.stringify({
        campaign: this.campaignKey,
        submitted_at: new Date().toISOString()
      });

      if (this.lockStorageKey) {
        safeStorageSet(this.lockStorageKey, record);
      }

      if (this.lockCookieName) {
        setCookie(this.lockCookieName, record);
      }

      dispatch(this.page, 'abs:contactlocked', {
        campaignKey: this.campaignKey,
        deviceId: this.deviceId,
        values
      });
    }

    ensureDeviceField() {
      let field = qs(
        'input[name="device_id"]',
        this.form
      );

      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'device_id';
        field.dataset.absGenerated = 'true';
        this.form.appendChild(field);
      }

      field.value = this.deviceId;
    }

    showAlreadySubmitted() {
      this.form.classList.add('is-success');
      this.form.dataset.absSubmissionLocked = 'true';

      qsa(
        'input, select, textarea, button',
        this.form
      ).forEach((control) => {
        if (control.type === 'hidden') return;
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      });

      if (this.success) {
        this.success.setAttribute('aria-hidden', 'false');

        const title = qs(
          '.abs-contact-success__title',
          this.success
        );
        const copy = qs(
          '.abs-contact-success__copy',
          this.success
        );
        const icon = qs(
          '.abs-contact-success__icon',
          this.success
        );

        if (icon) {
          icon.textContent = '✓';
        }

        if (title) {
          title.textContent = 'Enquiry already submitted.';
        }

        if (copy) {
          copy.textContent =
            'An enquiry from this device has already been submitted for this campaign. Another submission is not allowed.';
        }
      }

      this.islandSteps.forEach((step) => {
        step.classList.remove('is-active');
        step.classList.add('is-complete');
        step.setAttribute('aria-disabled', 'true');
      });

      if (this.mobileLabel) {
        this.mobileLabel.textContent = 'Submitted';
      }

      if (this.mobileCount) {
        this.mobileCount.textContent =
          `${String(this.steps.length).padStart(2, '0')} / ${String(this.steps.length).padStart(2, '0')}`;
      }

      if (this.island) {
        this.island.style.setProperty(
          '--abs-island-progress',
          '100%'
        );
      }

      if (this.islandProgressBar) {
        this.islandProgressBar.style.width = '100%';
      }

      this.page.dataset.absContactLocked = 'true';
    }

    /* ----------------------------------------------------------------------
       INITIALIZATION
       ---------------------------------------------------------------------- */

    getInitialIndex() {
      if (!this.steps.length) return 0;

      const active = this.steps.findIndex((step) =>
        step.classList.contains('is-active')
      );

      return active >= 0 ? active : 0;
    }

    init() {
      if (!this.steps.length) return;

      activateContactOnlyMode();

      this.page.dataset.absContactReady = 'true';
      this.form.dataset.absContactReady = 'true';

      this.ensureDeviceField();

      this.steps.forEach((step, index) => {
        step.dataset.absStepIndex = String(index);

        if (!step.dataset.absStepLabel) {
          step.dataset.absStepLabel =
            step.dataset.stepLabel ||
            text(qs('.abs-contact-step__kicker', step)?.textContent) ||
            `Step ${index + 1}`;
        }
      });

      this.islandSteps.forEach((step, index) => {
        step.dataset.absIslandIndex = String(index);

        if (!step.hasAttribute('type') && step.tagName === 'BUTTON') {
          step.type = 'button';
        }
      });

      this.form.addEventListener('click', this.handleClick);
      this.form.addEventListener('input', this.handleInput);
      this.form.addEventListener('change', this.handleChange);
      this.form.addEventListener('keydown', this.handleKeydown);
      this.form.addEventListener('submit', this.handleSubmit);

      if (this.island) {
        this.island.addEventListener('click', this.handleClick);

        if (!prefersReducedMotion.matches) {
          this.island.addEventListener(
            'pointermove',
            this.handleIslandPointerMove,
            { passive: true }
          );

          this.island.addEventListener(
            'pointerleave',
            this.handleIslandPointerLeave,
            { passive: true }
          );
        }
      }

      if (this.isSubmissionLocked()) {
        this.showAlreadySubmitted();

        requestAnimationFrame(() => {
          this.page.classList.add('is-ready');
        });

        dispatch(this.page, 'abs:contactready', {
          steps: this.steps.length,
          currentIndex: this.currentIndex,
          locked: true
        });

        return;
      }

      this.setupOptions();
      this.updateUI(false);
      this.refreshNextState();

      requestAnimationFrame(() => {
        this.page.classList.add('is-ready');
      });

      dispatch(this.page, 'abs:contactready', {
        steps: this.steps.length,
        currentIndex: this.currentIndex
      });
    }

    destroy() {
      this.form.removeEventListener('click', this.handleClick);
      this.form.removeEventListener('input', this.handleInput);
      this.form.removeEventListener('change', this.handleChange);
      this.form.removeEventListener('keydown', this.handleKeydown);
      this.form.removeEventListener('submit', this.handleSubmit);

      if (this.island) {
        this.island.removeEventListener('click', this.handleClick);
        this.island.removeEventListener(
          'pointermove',
          this.handleIslandPointerMove
        );
        this.island.removeEventListener(
          'pointerleave',
          this.handleIslandPointerLeave
        );
      }
    }

    setupOptions() {
      qsa(SELECTORS.option, this.form).forEach((option) => {
        option.setAttribute('role', option.getAttribute('role') || 'radio');

        if (!option.hasAttribute('tabindex')) {
          option.setAttribute('tabindex', '0');
        }

        option.setAttribute(
          'aria-checked',
          option.classList.contains('is-selected') ? 'true' : 'false'
        );
      });

      this.syncOptionsFromHiddenFields();
    }

    /* ==========================================================================
       5. CLICK / INPUT HANDLING
       ========================================================================== */

    handleClick(event) {
      const option = event.target.closest(SELECTORS.option);

      if (option && this.form.contains(option)) {
        event.preventDefault();
        this.selectOption(option);
        return;
      }

      const next = event.target.closest(SELECTORS.next);

      if (next && this.form.contains(next)) {
        event.preventDefault();
        this.next();
        return;
      }

      const back = event.target.closest(SELECTORS.back);

      if (back && this.form.contains(back)) {
        event.preventDefault();
        this.back();
        return;
      }

      const islandStep = event.target.closest(SELECTORS.islandStep);

      if (
        islandStep &&
        this.island &&
        this.island.contains(islandStep)
      ) {
        event.preventDefault();

        const index = Number(islandStep.dataset.absIslandIndex);

        /*
          Step-by-step rule:
          Users may revisit completed steps, but cannot skip forward into
          unanswered steps.
        */
        if (
          Number.isInteger(index) &&
          index <= this.highestVisitedIndex &&
          index !== this.currentIndex
        ) {
          this.goTo(index, 'island');
        }
      }
    }

    handleInput(event) {
      const control = event.target.closest(SELECTORS.control);

      if (!control || !this.form.contains(control)) return;

      this.clearControlError(control);
      this.refreshNextState();
    }

    handleChange(event) {
      const control = event.target.closest('input, select, textarea');

      if (!control || !this.form.contains(control)) return;

      this.clearControlError(control);
      this.syncOptionsFromHiddenFields();
      this.refreshNextState();
    }

    handleKeydown(event) {
      const option = event.target.closest(SELECTORS.option);

      if (
        option &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        this.selectOption(option);
        return;
      }

      if (event.key === 'Enter') {
        const target = event.target;

        if (
          target.matches('input, select') &&
          this.currentIndex < this.steps.length - 1
        ) {
          event.preventDefault();
          this.next();
        }
      }
    }

    /* ==========================================================================
       5. OPTION SELECTION
       ========================================================================== */

    selectOption(option) {
      if (option.getAttribute('aria-disabled') === 'true') return;

      const group =
        option.closest(SELECTORS.optionGroup) ||
        option.parentElement;

      if (!group) return;

      const name =
        option.dataset.name ||
        group.dataset.absOptionGroup ||
        group.dataset.name ||
        '';

      const value =
        option.dataset.value ||
        option.dataset.optionValue ||
        text(qs('.abs-contact-option__title', option)?.textContent) ||
        text(option.textContent);

      qsa(SELECTORS.option, group).forEach((item) => {
        const selected = item === option;

        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      this.writeValue(name, value);

      group.classList.remove('is-invalid');

      dispatch(this.form, 'abs:contactoptionchange', {
        name,
        value,
        option,
        stepIndex: this.currentIndex
      });

      this.refreshNextState();

      /*
        Default behavior remains manual Next button because the user explicitly
        wants: choose → click Next → next question.
        Auto-advance is available only if Liquid sets data-auto-advance="true".
      */
      if (
        option.dataset.autoAdvance === 'true' ||
        group.dataset.autoAdvance === 'true'
      ) {
        window.setTimeout(() => this.next(), 180);
      }
    }

    writeValue(name, value) {
      if (!name) return;

      const escaped = safeCSS(name);

      let field = qs(`[name="${escaped}"]`, this.form);

      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.dataset.absGenerated = 'true';
        this.form.appendChild(field);
      }

      field.value = value;

      field.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    syncOptionsFromHiddenFields() {
      qsa(SELECTORS.optionGroup, this.form).forEach((group) => {
        const name = group.dataset.absOptionGroup || group.dataset.name || '';

        if (!name) return;

        const field = qsa('[name]', this.form).find(
          (input) => input.name === name
        );

        if (!field) return;

        const selectedValue = text(field.value);

        qsa(SELECTORS.option, group).forEach((option) => {
          const optionValue =
            option.dataset.value ||
            option.dataset.optionValue ||
            text(qs('.abs-contact-option__title', option)?.textContent) ||
            text(option.textContent);

          const selected = selectedValue === optionValue;

          option.classList.toggle('is-selected', selected);
          option.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
      });
    }

    /* ==========================================================================
       6. NAVIGATION
       ========================================================================== */

    currentStep() {
      return this.steps[this.currentIndex] || null;
    }

    next() {
      if (this.animating) return;

      const step = this.currentStep();

      if (!step) return;

      if (!this.validateStep(step, true)) {
        this.focusFirstError(step);
        return;
      }

      if (this.currentIndex >= this.steps.length - 1) {
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.dispatchEvent(
            new Event('submit', {
              bubbles: true,
              cancelable: true
            })
          );
        }

        return;
      }

      this.goTo(this.currentIndex + 1, 'next');
    }

    back() {
      if (this.animating || this.currentIndex <= 0) return;

      this.goTo(this.currentIndex - 1, 'back');
    }

    goTo(index, source = 'api') {
      const nextIndex = clamp(index, 0, this.steps.length - 1);

      if (nextIndex === this.currentIndex) return;

      const oldStep = this.currentStep();
      const newStep = this.steps[nextIndex];

      if (!oldStep || !newStep) return;

      const movingForward = nextIndex > this.currentIndex;

      this.animating = true;

      oldStep.classList.remove('is-active');
      oldStep.classList.toggle('is-leaving', movingForward);
      oldStep.classList.toggle('is-entering-back', !movingForward);
      oldStep.setAttribute('aria-hidden', 'true');

      newStep.classList.remove('is-leaving', 'is-entering-back');

      if (!movingForward) {
        newStep.classList.add('is-entering-back');
      }

      this.currentIndex = nextIndex;
      this.highestVisitedIndex = Math.max(
        this.highestVisitedIndex,
        this.currentIndex
      );

      requestAnimationFrame(() => {
        newStep.classList.add('is-active');
        newStep.classList.remove('is-entering-back');
        newStep.setAttribute('aria-hidden', 'false');

        this.updateUI(true);

        window.setTimeout(() => {
          oldStep.classList.remove(
            'is-leaving',
            'is-entering-back'
          );

          this.animating = false;
        }, prefersReducedMotion.matches ? 0 : 630);
      });

      dispatch(this.page, 'abs:contactstepchange', {
        activeIndex: this.currentIndex,
        source
      });

      this.scrollIntoComfortableView();
    }

    /* ==========================================================================
       7. DYNAMIC ISLAND + STEP UI
       ========================================================================== */

    updateUI(shouldFocus = true) {
      const total = this.steps.length;
      const current = this.currentIndex + 1;
      const progress = total ? (current / total) * 100 : 0;

      this.steps.forEach((step, index) => {
        const active = index === this.currentIndex;

        step.classList.toggle('is-active', active);
        step.setAttribute('aria-hidden', active ? 'false' : 'true');

        qsa(
          'button, input, select, textarea, a[href]',
          step
        ).forEach((control) => {
          if (active) {
            if (control.dataset.absOriginalTabindex !== undefined) {
              const original = control.dataset.absOriginalTabindex;

              if (original === '') {
                control.removeAttribute('tabindex');
              } else {
                control.setAttribute('tabindex', original);
              }

              delete control.dataset.absOriginalTabindex;
            }
          } else {
            if (control.dataset.absOriginalTabindex === undefined) {
              control.dataset.absOriginalTabindex =
                control.getAttribute('tabindex') || '';
            }

            control.setAttribute('tabindex', '-1');
          }
        });
      });

      this.islandSteps.forEach((islandStep, index) => {
        const active = index === this.currentIndex;
        const complete = index < this.currentIndex;
        const reachable = index <= this.highestVisitedIndex;

        islandStep.classList.toggle('is-active', active);
        islandStep.classList.toggle('is-complete', complete);

        islandStep.setAttribute(
          'aria-current',
          active ? 'step' : 'false'
        );

        islandStep.setAttribute(
          'aria-disabled',
          reachable ? 'false' : 'true'
        );

        if (reachable && !active) {
          islandStep.style.cursor = 'pointer';
        } else {
          islandStep.style.cursor = 'default';
        }
      });

      if (this.currentEl) {
        this.currentEl.textContent =
          String(current).padStart(2, '0');
      }

      if (this.totalEl) {
        this.totalEl.textContent =
          String(total).padStart(2, '0');
      }

      if (this.mobileCount) {
        this.mobileCount.textContent =
          `${String(current).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      }

      if (this.mobileLabel) {
        this.mobileLabel.textContent = this.getActiveLabel();
      }

      if (this.island) {
        this.island.style.setProperty(
          '--abs-step-count',
          String(total)
        );

        this.island.style.setProperty(
          '--abs-island-progress',
          `${progress}%`
        );

        const glowX =
          total <= 1
            ? 50
            : ((this.currentIndex + 0.5) / total) * 100;

        this.island.style.setProperty(
          '--abs-island-glow-x',
          `${glowX}%`
        );
      }

      if (this.islandProgress) {
        this.islandProgress.style.setProperty(
          '--abs-island-progress',
          `${progress}%`
        );
      }

      if (this.islandProgressBar) {
        this.islandProgressBar.style.width = `${progress}%`;
      }

      this.refreshNextState();

      if (shouldFocus) {
        this.focusCurrentStep();
      }
    }

    getActiveLabel() {
      const island = this.islandSteps[this.currentIndex];

      if (island) {
        return (
          island.dataset.label ||
          text(qs('.abs-island-step__label', island)?.textContent) ||
          `Step ${this.currentIndex + 1}`
        );
      }

      const step = this.currentStep();

      return (
        step?.dataset.absStepLabel ||
        `Step ${this.currentIndex + 1}`
      );
    }

    handleIslandPointerMove(event) {
      if (!this.island) return;
      if (event.pointerType && event.pointerType !== 'mouse') return;

      const rect = this.island.getBoundingClientRect();

      const x = clamp(
        ((event.clientX - rect.left) / rect.width) * 100,
        0,
        100
      );

      this.island.style.setProperty(
        '--abs-island-glow-x',
        `${x}%`
      );
    }

    handleIslandPointerLeave() {
      if (!this.island) return;

      const total = this.steps.length || 1;

      const glowX =
        total <= 1
          ? 50
          : ((this.currentIndex + 0.5) / total) * 100;

      this.island.style.setProperty(
        '--abs-island-glow-x',
        `${glowX}%`
      );
    }

    /* ==========================================================================
       8. VALIDATION
       ========================================================================== */

    refreshNextState() {
      const step = this.currentStep();

      if (!step) return;

      const nextButton = qs(SELECTORS.next, step);

      if (!nextButton) return;

      const requireValid =
        step.dataset.disableNextUntilValid === 'true';

      if (!requireValid) {
        setDisabled(nextButton, false);
        return;
      }

      const valid = this.validateStep(step, false);

      setDisabled(nextButton, !valid);
    }

    validateStep(step, showErrors = true) {
      let valid = true;

      /*
        Required option groups
      */
      qsa(
        `${SELECTORS.optionGroup}[data-required="true"]`,
        step
      ).forEach((group) => {
        const selected = qs(
          `${SELECTORS.option}.is-selected`,
          group
        );

        if (!selected) {
          valid = false;

          if (showErrors) {
            group.classList.add('is-invalid');
          }
        } else if (showErrors) {
          group.classList.remove('is-invalid');
        }
      });

      /*
        Normal required controls
      */
      qsa(
        'input[required], select[required], textarea[required]',
        step
      ).forEach((control) => {
        if (!this.controlValid(control)) {
          valid = false;

          if (showErrors) {
            this.showControlError(control);
          }
        } else if (showErrors) {
          this.clearControlError(control);
        }
      });

      return valid;
    }

    controlValid(control) {
      if (!control) return true;

      if (control.type === 'checkbox') {
        return control.required ? control.checked : true;
      }

      if (
        typeof control.checkValidity === 'function' &&
        !control.checkValidity()
      ) {
        return false;
      }

      if (control.required && !text(control.value)) {
        return false;
      }

      return true;
    }

    showControlError(control) {
      control.classList.add('is-invalid');
      control.setAttribute('aria-invalid', 'true');

      const field = control.closest(SELECTORS.field);

      if (field) {
        field.classList.add('is-invalid');
      }
    }

    clearControlError(control) {
      control.classList.remove('is-invalid');
      control.removeAttribute('aria-invalid');

      const field = control.closest(SELECTORS.field);

      if (field) {
        field.classList.remove('is-invalid');
      }
    }

    focusFirstError(step) {
      const target =
        qs('.abs-contact-field.is-invalid .abs-contact-field__control', step) ||
        qs('.abs-contact-option.is-selected', step) ||
        qs('.abs-contact-option', qs('.is-invalid', step) || step);

      if (!target) return;

      try {
        target.focus({
          preventScroll: false
        });
      } catch (_) {
        target.focus();
      }
    }

    focusCurrentStep() {
      if (!window.matchMedia('(pointer: fine)').matches) return;

      const step = this.currentStep();

      if (!step) return;

      const target =
        qs('[data-abs-step-focus]', step) ||
        qs(
          '.abs-contact-option, .abs-contact-field__control',
          step
        );

      if (!target) return;

      window.setTimeout(() => {
        try {
          target.focus({
            preventScroll: true
          });
        } catch (_) {
          target.focus();
        }
      }, prefersReducedMotion.matches ? 0 : 240);
    }

    /* ==========================================================================
       10. SUBMISSION
       ========================================================================== */

    async handleSubmit(event) {
      if (this.isSubmissionLocked()) {
        event.preventDefault();
        this.showAlreadySubmitted();
        return;
      }

      const step = this.currentStep();

      if (step && !this.validateStep(step, true)) {
        event.preventDefault();
        this.focusFirstError(step);
        return;
      }

      const mode =
        this.form.dataset.absSubmitMode ||
        this.page.dataset.absSubmitMode ||
        'demo';

      const values = this.serialize();

      dispatch(this.form, 'abs:contactsubmit', {
        form: this.form,
        page: this.page,
        values
      });

      if (mode === 'demo') {
        event.preventDefault();
        this.showSuccess(values);
        return;
      }

      if (mode === 'google-sheets') {
        event.preventDefault();

        const endpoint = text(
          this.form.dataset.absEndpoint ||
          this.page.dataset.absEndpoint
        );

        if (!endpoint) {
          console.error(
            '[ABS Contact Flow] Google Sheets endpoint is missing.'
          );
          this.showSubmitError(
            'We could not send your enquiry. Please try again.'
          );
          return;
        }

        await this.submitToGoogleSheets(endpoint, values);
      }
    }

    async submitToGoogleSheets(endpoint, values) {
      const submitButton = qs(
        'button[type="submit"]',
        this.currentStep() || this.form
      );

      const originalHTML = submitButton
        ? submitButton.innerHTML
        : '';

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.innerHTML =
          '<span>Sending...</span><span aria-hidden="true">→</span>';
      }

      this.clearSubmitError();

      try {
        const payload = new URLSearchParams();

        Object.entries(values).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              payload.append(key, item == null ? '' : String(item));
            });
          } else {
            payload.append(
              key,
              value == null ? '' : String(value)
            );
          }
        });

        /*
          Google Apps Script web apps do not expose CORS headers by default.
          no-cors allows the browser to send the form payload without a
          preflight request. The response is intentionally opaque, so the UI
          treats a completed network send as success.
        */
        /*
          Google Apps Script often writes the lead to the Sheet almost
          immediately, but the browser may wait several seconds for the
          Apps Script HTTP response to fully close.

          Start the request normally, but do not make the customer wait for
          that slow response. If the browser does not report an immediate
          network error, show the success screen after a short confirmation
          delay while the POST continues in the background.
        */
        const request = fetch(endpoint, {
          method: 'POST',
          mode: 'no-cors',
          cache: 'no-store',
          keepalive: true,
          body: payload
        });

        const result = await Promise.race([
          request
            .then(() => ({ type: 'sent' }))
            .catch((error) => ({ type: 'error', error })),
          new Promise((resolve) => {
            window.setTimeout(
              () => resolve({ type: 'background' }),
              450
            );
          })
        ]);

        if (result.type === 'error') {
          throw result.error;
        }

        /*
          At this point either:
          1. the request already completed, or
          2. it is still finishing in the background.

          In both cases the customer gets an immediate success experience.
        */
        this.markSubmissionLocked(values);
        this.showSuccess(values);

        /*
          If the request is still running, prevent a later rejection from
          becoming an unhandled promise rejection. We intentionally do not
          pull the user back out of the success screen after submission.
        */
        request.catch((error) => {
          console.error(
            '[ABS Contact Flow] Background Google Sheets request error:',
            error
          );
        });

      } catch (error) {
        console.error(
          '[ABS Contact Flow] Google Sheets submission failed:',
          error
        );

        this.showSubmitError(
          'We could not send your enquiry. Please check your connection and try again.'
        );

      } finally {
        if (
          submitButton &&
          !this.form.classList.contains('is-success')
        ) {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
          submitButton.innerHTML = originalHTML;
        }
      }
    }

    showSubmitError(message) {
      let error = qs('[data-abs-submit-error]', this.form);

      if (!error) {
        error = document.createElement('p');
        error.dataset.absSubmitError = 'true';
        error.setAttribute('role', 'alert');
        error.style.margin = '16px 0 0';
        error.style.color = '#efabab';
        error.style.fontSize = '11px';
        error.style.lineHeight = '1.6';

        const actions = qs(
          '.abs-contact-actions',
          this.currentStep() || this.form
        );

        if (actions) {
          actions.insertAdjacentElement('beforebegin', error);
        } else {
          this.form.appendChild(error);
        }
      }

      error.textContent = message;
    }

    clearSubmitError() {
      const error = qs('[data-abs-submit-error]', this.form);

      if (error) {
        error.remove();
      }
    }

    serialize() {
      this.ensureDeviceField();

      const result = {};
      const data = new FormData(this.form);

      data.forEach((value, key) => {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
          if (!Array.isArray(result[key])) {
            result[key] = [result[key]];
          }

          result[key].push(value);
        } else {
          result[key] = value;
        }
      });

      return result;
    }

    showSuccess(values = this.serialize()) {
      this.form.classList.add('is-success');

      if (this.success) {
        this.success.setAttribute('aria-hidden', 'false');
      }

      /*
        Mark every Dynamic Island stage as completed for a satisfying final state.
      */
      this.islandSteps.forEach((step) => {
        step.classList.remove('is-active');
        step.classList.add('is-complete');
      });

      if (this.mobileLabel) {
        this.mobileLabel.textContent = 'Complete';
      }

      if (this.mobileCount) {
        this.mobileCount.textContent =
          `${String(this.steps.length).padStart(2, '0')} / ${String(this.steps.length).padStart(2, '0')}`;
      }

      if (this.island) {
        this.island.style.setProperty(
          '--abs-island-progress',
          '100%'
        );
      }

      if (this.islandProgressBar) {
        this.islandProgressBar.style.width = '100%';
      }

      dispatch(this.page, 'abs:contactsuccess', {
        values
      });

      this.scrollIntoComfortableView(true);
    }

    reset() {
      if (this.isSubmissionLocked()) {
        this.showAlreadySubmitted();
        return;
      }

      this.form.reset();

      qsa(SELECTORS.option, this.form).forEach((option) => {
        option.classList.remove('is-selected');
        option.setAttribute('aria-checked', 'false');
      });

      qsa('[data-abs-generated="true"]', this.form).forEach(
        (field) => field.remove()
      );

      qsa('.is-invalid', this.form).forEach((element) => {
        element.classList.remove('is-invalid');
        element.removeAttribute('aria-invalid');
      });

      this.form.classList.remove('is-success');

      if (this.success) {
        this.success.setAttribute('aria-hidden', 'true');
      }

      this.currentIndex = 0;
      this.highestVisitedIndex = 0;

      this.updateUI(false);
    }

    /* ==========================================================================
       11. VIEWPORT COMFORT
       ========================================================================== */

    scrollIntoComfortableView(force = false) {
      const rect = this.form.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight;

      const idealTop =
        window.scrollY +
        rect.top -
        Math.min(130, viewportHeight * 0.14);

      const needsAdjustment =
        rect.top < 90 ||
        rect.top > viewportHeight * 0.36;

      if (!force && !needsAdjustment) return;

      window.setTimeout(() => {
        window.scrollTo({
          top: Math.max(0, idealTop),
          behavior: prefersReducedMotion.matches
            ? 'auto'
            : 'smooth'
        });
      }, 40);
    }
  }

  /* ==========================================================================
     11. PAGE INITIALIZATION
     ========================================================================== */

  function initPage(page) {
    if (!page || STATE.pages.has(page)) return;

    STATE.pages.add(page);

    const form = qs(SELECTORS.form, page);

    if (!form) return;

    const instance = new ContactFlow(page);

    STATE.instances.set(page, instance);
  }

  function init(scope = document) {
    const pages = [];

    if (scope.matches && scope.matches(SELECTORS.page)) {
      pages.push(scope);
    }

    qsa(SELECTORS.page, scope).forEach((page) => {
      pages.push(page);
    });

    [...new Set(pages)].forEach(initPage);
  }

  /* ==========================================================================
     12. SHOPIFY THEME EDITOR
     ========================================================================== */

  function onSectionLoad(event) {
    const section = event.target;

    if (!section) return;

    init(section);
  }

  function onSectionUnload(event) {
    const section = event.target;

    if (!section) return;

    const pages = [];

    if (section.matches?.(SELECTORS.page)) {
      pages.push(section);
    }

    qsa(SELECTORS.page, section).forEach((page) => {
      pages.push(page);
    });

    pages.forEach((page) => {
      const instance = STATE.instances.get(page);

      if (instance) {
        instance.destroy();
        STATE.instances.delete(page);
      }
    });

    window.setTimeout(
      deactivateContactOnlyModeIfUnused,
      0
    );
  }

  document.addEventListener(
    'shopify:section:load',
    onSectionLoad
  );

  document.addEventListener(
    'shopify:section:unload',
    onSectionUnload
  );

  /* ==========================================================================
     13. BOOT
     ========================================================================== */

  function boot() {
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      boot,
      { once: true }
    );
  } else {
    boot();
  }

  /* ==========================================================================
     14. PUBLIC API
     ========================================================================== */

  ABS.init = init;

  ABS.get = (pageOrSelector) => {
    const page =
      typeof pageOrSelector === 'string'
        ? document.querySelector(pageOrSelector)
        : pageOrSelector;

    return page
      ? STATE.instances.get(page) || null
      : null;
  };

  ABS.goTo = (pageOrSelector, index) => {
    const instance = ABS.get(pageOrSelector);

    if (instance) {
      instance.goTo(index, 'public-api');
    }
  };

  ABS.reset = (pageOrSelector) => {
    const instance = ABS.get(pageOrSelector);

    if (instance) {
      instance.reset();
    }
  };
})();
